import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Company {
  id: string;
  name: string;
  email: string;
  industry: string;
  logo?: {
    url: string;
    filename: string;
  };
  totalEmployees: number;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  authorizationStatus: string;
  company?: Company;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string; authorizationStatus?: string }>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  isCompanyAdmin: () => boolean;
  isCompanyManager: () => boolean;
  getCompanyLogo: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token with backend
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token: string) => {
    try {
      const response = await fetch('/api/auth/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string; authorizationStatus?: string }> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        return { success: true };
      } else {
        // Handle authorization status responses
        if (data.authorizationStatus) {
          return { 
            success: false, 
            message: data.message,
            authorizationStatus: data.authorizationStatus 
          };
        }
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    
    switch (permission) {
      case 'admin':
        return user.role === 'admin';
      case 'manager':
        return user.role === 'admin' || user.role === 'manager';
      case 'user':
        return user.role === 'admin' || user.role === 'manager' || user.role === 'user';
      case 'company_admin':
        return user.role === 'admin' || user.role === 'manager';
      default:
        return false;
    }
  };

  const isCompanyAdmin = (): boolean => {
    return user?.role === 'admin' || false;
  };

  const isCompanyManager = (): boolean => {
    return user?.role === 'manager' || false;
  };

  const getCompanyLogo = (): string | null => {
    return user?.company?.logo?.url || null;
  };

  const value = {
    user,
    isAuthenticated: !!user && user.authorizationStatus === 'approved',
    loading,
    login,
    logout,
    hasPermission,
    isCompanyAdmin,
    isCompanyManager,
    getCompanyLogo
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;